import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { track } from '@ha/analytics';
import { useIntl } from 'ha/i18n';
import { Button, ButtonGroup, Typography, units } from '@hbf/dsl';

import { updateMapView } from '../actions';
import { getMapViewState, getCityCanonical } from '../selectors';
import { MapViewState, mapControlsArray } from '../utils';

const useStyles = makeStyles()(theme => ({
  button: {
    border: units.border(1, 'solid', theme.palette.grey[300]),
    borderBottom: 0,
    boxShadow: `inset 0 -1px 0 0 ${theme.palette.grey[300]}`,
    padding: units.yx(0.425, 0.75),
    minWidth: 'auto',
  },
  buttonActive: {
    boxShadow: `inset 0 -2px 0 0 ${theme.palette.secondary.main}`,
  },
  icon: {
    fontSize: units.rem(1),
    marginRight: units.rem(0.5),
  },
}));

const SearchMapViewControls: React.FC = () => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const mapViewCurrentState = useSelector(getMapViewState);
  const cityCanonical = useSelector(getCityCanonical);

  const handleMapViewChange = (value: MapViewState) => {
    dispatch(updateMapView(value));

    track('Changed map view on search ', {
      mapType: value,
      cityCanonical,
    });
  };

  return (
    <ButtonGroup variant="outlined" data-test-locator="Map view controls">
      {mapControlsArray.map(({ value, Icon, text }) => (
        <Button
          key={value}
          className={cx(classes.button, {
            [classes.buttonActive]: value === mapViewCurrentState,
          })}
          onClick={() => handleMapViewChange(value)}
          data-test-locator={`Map view btn ${value}`}
        >
          <Icon
            className={classes.icon}
            color={value === mapViewCurrentState ? 'secondary' : 'action'}
          />
          <Typography.Caption
            textTransform="none"
            color={value === mapViewCurrentState ? 'mint' : 'default'}
          >
            {T(text)}
          </Typography.Caption>
        </Button>
      ))}
    </ButtonGroup>
  );
};

export { SearchMapViewControls };
